import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { telecomCarriersService } from '../services/api';
import { fmtCNPJ, maskCNPJ } from '../utils/formatters';
import CascaDeModal from './shared/CascaDeModal';
import { campo, mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT, TONE_BADGE } from '../utils/statusColors';
import { usoDaPrestadora, ddrsPorExtenso, fraseDoRenomear } from '../utils/configuracoes';

interface Carrier {
  id: number;
  nome: string;
  razaoSocial?: string | null;
  cnpj?: string | null;
  ativo: boolean;
  notes?: string | null;
}

interface Props {
  canEdit: boolean;
  /** D4: os DDRs do App, para contar quem aponta para cada prestadora. `null` = não foram lidos. */
  ddrs: Array<{ carrierId?: number | null; hostCarrierId?: number | null }> | null;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  Icon: React.ComponentType<{ name: string; className?: string }>;
}

const emptyForm = { nome: '', razaoSocial: '', cnpj: '', ativo: true, notes: '' };

export default function PrestadorasTab({ canEdit, ddrs, showToast, Icon }: Props) {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [editing, setEditing] = useState<Carrier | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [delConfirm, setDelConfirm] = useState<Carrier | null>(null);
  // A lista que não carregava dava um toast e "Nenhuma prestadora cadastrada", que convida a cadastrar outra.
  const [erro, setErro] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await telecomCarriersService.getAll();
      setCarriers(Array.isArray(data) ? data : []);
      setErro(null);
    } catch (e: any) {
      setErro(e?.response?.data?.message || mensagemDoErroDeCarga(e));
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return carriers;
    return carriers.filter((c) =>
      c.nome.toLowerCase().includes(q) ||
      (c.razaoSocial || '').toLowerCase().includes(q) ||
      (c.cnpj || '').replace(/\D/g, '').includes(q.replace(/\D/g, '')),
    );
  }, [carriers, search]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModal('create'); };
  const openEdit = (c: Carrier) => {
    setEditing(c);
    setForm({ nome: c.nome, razaoSocial: c.razaoSocial || '', cnpj: c.cnpj ? fmtCNPJ(c.cnpj) : '', ativo: c.ativo, notes: c.notes || '' });
    setModal('edit');
  };

  const save = async () => {
    if (!form.nome.trim()) { showToast('Informe o nome da prestadora', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        razaoSocial: form.razaoSocial.trim() || undefined,
        cnpj: form.cnpj.replace(/\D/g, '') ? form.cnpj.trim() : undefined,
        ativo: form.ativo,
        notes: form.notes.trim() || undefined,
      };
      if (modal === 'edit' && editing) {
        await telecomCarriersService.update(editing.id, payload);
        showToast('Prestadora atualizada');
      } else {
        await telecomCarriersService.create(payload);
        showToast('Prestadora cadastrada');
      }
      setModal(null);
      load();
    } catch (e: any) {
      showToast('Falha ao salvar: ' + (e?.response?.data?.message || e?.message || ''), 'error');
    } finally {
      setSaving(false);
    }
  };

  // D4: com DDR apontando, o caminho é desativar (some dos dropdowns, os DDRs seguem com o nome); excluir deixaria
  // os DDRs com um ID órfão, porque não há FK de client_ddrs para telecom_carriers.
  const doDeactivate = async (c: Carrier) => {
    try {
      await telecomCarriersService.update(c.id, { ativo: false });
      showToast('Prestadora desativada');
      setDelConfirm(null);
      load();
    } catch (e: any) {
      showToast('Falha ao desativar: ' + (e?.response?.data?.message || e?.message || ''), 'error');
    }
  };

  const doDelete = async (c: Carrier) => {
    try {
      await telecomCarriersService.delete(c.id);
      showToast('Prestadora excluída');
      setDelConfirm(null);
      load();
    } catch (e: any) {
      showToast('Falha ao excluir: ' + (e?.response?.data?.message || e?.message || ''), 'error');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-white">Prestadoras</h2>
          <p className="text-sm text-slate-400">Operadoras de telecom (nome, razão social, CNPJ) — usadas na Operadora Real dos DDRs.</p>
        </div>
        {canEdit && !erro && !loading && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-strong text-white rounded-lg text-sm transition-colors">
            <Icon name="plus" className="w-4 h-4" /> Nova Prestadora
          </button>
        )}
      </div>

      <div className="relative mb-4 max-w-md">
        <Icon name="search" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, razão social ou CNPJ..." className={`w-full rounded-lg pl-9 pr-3 py-2 text-sm placeholder-slate-400 ${campo}`} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/80">
            <tr className="text-slate-400 text-xs uppercase">
              <th className="px-4 py-3 text-left">Nome</th>
              <th className="px-4 py-3 text-left">Razão Social</th>
              <th className="px-4 py-3 text-left">CNPJ</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Carregando…</td></tr>
            ) : erro ? (
              <tr><td colSpan={5} role="alert" className={`px-4 py-8 text-center ${TONE_TEXT.danger}`}>
                Não consegui carregar as prestadoras ({erro}).{' '}
                <button onClick={load} className="underline">Tentar de novo</button>
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Nenhuma prestadora{search ? ' encontrada' : ' cadastrada'}.</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="hover:bg-slate-800/40">
                <td className="px-4 py-3 text-slate-200 font-medium">{c.nome}</td>
                <td className="px-4 py-3 text-slate-400">{c.razaoSocial || '—'}</td>
                <td className="px-4 py-3 text-slate-400 font-mono">{c.cnpj ? fmtCNPJ(c.cnpj) : '—'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.ativo ? TONE_BADGE.open : TONE_BADGE.neutral}`}>{c.ativo ? 'Ativo' : 'Inativo'}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  {canEdit && (
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-slate-400 hover:text-white" title="Editar"><Icon name="edit" className="w-4 h-4" /></button>
                      <button onClick={() => setDelConfirm(c)} className="p-1.5 text-slate-400 hover:text-red-400" title="Excluir"><Icon name="trash" className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        // Formulário: antes fechava ao clicar fora (quando não estava gravando); agora
        // só pelo × ou Cancelar. z-60: abre por cima da moldura de Prestadoras do App.
        <CascaDeModal
          largura="sm"
          zIndex="z-[60]"
          titulo={modal === 'edit' ? 'Editar Prestadora' : 'Nova Prestadora'}
          onClose={() => { if (!saving) setModal(null); }}
          corpoClassName="p-4 space-y-3 text-sm"
          rodape={
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setModal(null)} disabled={saving} className="px-4 py-2 text-slate-300 hover:text-white text-sm disabled:opacity-40">Cancelar</button>
              <button onClick={save} disabled={saving || !form.nome.trim()} className="px-4 py-2 bg-primary hover:bg-primary-strong disabled:opacity-40 text-white rounded-lg text-sm">{saving ? 'Salvando…' : 'Salvar'}</button>
            </div>
          }
        >
              <div>
                <label className="text-xs text-slate-400 block mb-1">Nome *</label>
                <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: GT GROUP" className={`w-full rounded-lg px-3 py-2 placeholder-slate-500 ${campo}`} />
                {modal === 'edit' && editing && (
                  <p className="text-xs text-slate-400 mt-1" data-testid="frase-do-renomear">{fraseDoRenomear(usoDaPrestadora(editing.id, ddrs))}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Razão Social</label>
                <input type="text" value={form.razaoSocial} onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })} placeholder="Ex.: GT GROUP INTERNATIONAL BRASIL TELECOMUNICAÇÕES LTDA" className={`w-full rounded-lg px-3 py-2 placeholder-slate-500 ${campo}`} />
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">CNPJ</label>
                <input type="text" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: maskCNPJ(e.target.value) })} placeholder="00.000.000/0000-00" className={`w-full rounded-lg px-3 py-2 placeholder-slate-500 font-mono ${campo}`} />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} className="w-4 h-4" />
                Ativa (aparece nos dropdowns)
              </label>
        </CascaDeModal>
      )}

      {delConfirm && (() => {
        // D4 (11/10/2026): o confirm dizia "os DDRs não são afetados — é texto". Não é: eles apontam por ID, e não há
        // FK; excluir deixava os DDRs com ID órfão, sem o nome. Agora conta, e com uso trava e oferece desativar.
        const uso = usoDaPrestadora(delConfirm.id, ddrs);
        return (
        // Confirmação de perigo sem nada digitado: fecha ao clicar fora, como já era.
        <CascaDeModal
          largura="xs"
          zIndex="z-[60]"
          fecharAoClicarFora
          titulo={uso === 0 ? 'Excluir prestadora' : 'Prestadora em uso'}
          onClose={() => setDelConfirm(null)}
          corpoClassName="px-5 py-4"
          rodape={
            <div className="ml-auto flex gap-2">
              <button onClick={() => setDelConfirm(null)} className="px-4 py-2 text-slate-300 hover:text-white text-sm">Cancelar</button>
              {uso === 0 && (
                <button onClick={() => doDelete(delConfirm)} className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm">Excluir</button>
              )}
              {uso !== null && uso > 0 && delConfirm.ativo && (
                <button onClick={() => doDeactivate(delConfirm)} className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm">Desativar</button>
              )}
            </div>
          }
        >
          {uso === null ? (
            <p className="text-sm text-slate-400">Não consegui contar os DDRs que usam <strong className="text-slate-200">{delConfirm.nome}</strong>: a lista de DDRs não carregou. Não dá para excluir sem saber.</p>
          ) : uso > 0 ? (
            <p className="text-sm text-slate-400"><strong className="text-slate-200">{ddrsPorExtenso(uso)}</strong> apontam para <strong className="text-slate-200">{delConfirm.nome}</strong> (Operadora Real ou hospedagem). Excluir deixaria esses DDRs sem prestadora. {delConfirm.ativo ? 'Desative em vez de excluir: ela some dos dropdowns e os DDRs seguem com o nome.' : 'Ela já está inativa.'}</p>
          ) : (
            <p className="text-sm text-slate-400">Excluir <strong className="text-slate-200">{delConfirm.nome}</strong>? Nenhum DDR aponta para ela.</p>
          )}
        </CascaDeModal>
        ); })()}
    </div>
  );
}
